/**
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 調査詳細サイドの「関連チケット」カード。request.relatedTickets を種別バッジ付きで表示する。本件調査結果バグ（investigation_result_bug）は目立つ色で最上部、補足対応の対リンク（補足対応元 follow_up_of / 補足対応 follow_up）、参考バグ（reference_bug）・参考 ops（reference_ops）が続く。補足対応の対リンクは起票フローが張る自動リンクなので解除ボタンを出さない。backlog は外部 URL、ops は詳細ルートへリンクし、人手で紐付けた項目（linkedBy != null）は × で解除できる。本件調査で起票したバグがここに「本件調査結果」として初めて可視リンクされる。編集可能な依頼（Firestore 発番）では Backlog MCP 等で別途起票したバグを key / URL で本件調査結果バグとして紐付ける入力欄も出す（起票ボタンを通らない起票もバグ転換率に計上するため）。ops.requests.$id 本体から関連チケット表示を分離
 * @graph-connects OpsDetailPage [references] 依頼詳細ページ本体から関連チケットカードを分離
 * @graph-connects useRemoveOpsRelatedTicket [calls] 人手で紐付けた関連チケットの解除（DELETE）を実行
 * @graph-connects OpsLinkBugTicketForm [references] 本件調査結果バグの紐付け入力欄を編集可能な依頼でだけ出す
 */
import type { ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { Link } from '@tanstack/react-router';
import { Card, CardContent, CardHeader } from '@cortex/web-design-system/components/ui/Card';
import { opsDisplayNo } from '@cortex/ops-core';
import { useRemoveOpsRelatedTicket } from '../../../hooks/useOps.js';
import { OpsLinkBugTicketForm } from './OpsLinkBugTicketForm.js';
import type { OpsRelatedTicket, OpsRelatedTicketKind, OpsRequest } from '../../../types/ops.js';

/** 種別の表示順（本件調査結果バグを最優先、補足対応の対リンクが参考より前）。 @graph-connects none */
const KIND_ORDER: Record<OpsRelatedTicketKind, number> = {
  investigation_result_bug: 0,
  follow_up_of: 1,
  follow_up: 2,
  reference_bug: 3,
  reference_ops: 4,
};

/** 種別ごとのバッジのスタイル・i18n ラベルキー。 @graph-connects none */
const KIND_BADGE: Record<OpsRelatedTicketKind, { labelKey: string; className: string }> = {
  investigation_result_bug: {
    labelKey: 'ops.detail.related.badge.investigationResultBug',
    className: 'bg-[var(--color-error)]/15 text-[var(--color-error)] font-bold',
  },
  reference_bug: {
    labelKey: 'ops.detail.related.badge.referenceBug',
    className: 'bg-[var(--color-warning-muted)] text-[var(--color-warning)] font-semibold',
  },
  reference_ops: {
    labelKey: 'ops.detail.related.badge.referenceOps',
    className: 'bg-[var(--color-bg-hover)] text-[var(--color-text-muted)] font-semibold',
  },
  follow_up_of: {
    labelKey: 'ops.detail.related.badge.followUpOf',
    className:
      'bg-[var(--color-accent-primary)]/15 text-[var(--color-accent-primary)] font-semibold',
  },
  follow_up: {
    labelKey: 'ops.detail.related.badge.followUp',
    className:
      'bg-[var(--color-accent-primary)]/15 text-[var(--color-accent-primary)] font-semibold',
  },
};

/** 種別バッジ + 参照先リンク + 解除ボタンで 1 行を描画する。 @graph-connects none */
function RelatedTicketRow({
  requestId,
  ticket,
  t,
}: {
  requestId: string;
  ticket: OpsRelatedTicket;
  t: TFunction;
}): ReactNode {
  const removeMutation = useRemoveOpsRelatedTicket();
  const badge = KIND_BADGE[ticket.kind];
  const label = ticket.title ?? ticket.ref;
  return (
    <div className="flex items-start gap-1.5">
      <span className={`mt-0.5 shrink-0 rounded px-1 text-[10px] ${badge.className}`}>
        {t(badge.labelKey)}
      </span>
      <div className="min-w-0 flex-1">
        {ticket.target === 'backlog' ? (
          <a
            href={ticket.url ?? undefined}
            target="_blank"
            rel="noreferrer"
            className="block truncate text-[var(--color-accent-primary)] hover:underline"
            title={label}
          >
            {ticket.ref}
            {ticket.title !== null && ticket.title !== '' && (
              <span className="ml-1 text-[var(--color-text-secondary)]">— {ticket.title}</span>
            )}
          </a>
        ) : (
          <Link
            to="/ops/requests/$id"
            params={{ id: ticket.ref }}
            className="block truncate text-[var(--color-accent-primary)] hover:underline"
            title={label}
          >
            #{opsDisplayNo(ticket.ref)}
            {ticket.title !== null && ticket.title !== '' && (
              <span className="ml-1 text-[var(--color-text-secondary)]">— {ticket.title}</span>
            )}
          </Link>
        )}
      </div>
      {ticket.linkedBy !== null && (
        <button
          type="button"
          aria-label={t('ops.detail.related.unlink', { ref: ticket.ref })}
          disabled={removeMutation.isPending}
          onClick={() => {
            removeMutation.mutate({ id: requestId, target: ticket.target, ref: ticket.ref });
          }}
          className="shrink-0 rounded px-1 text-[var(--color-text-muted)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-error)] disabled:opacity-50"
        >
          ×
        </button>
      )}
    </div>
  );
}

/** OpsRelatedTicketsCard の props。 */
export interface OpsRelatedTicketsCardProps {
  /** 表示対象の依頼（id / relatedTickets を参照）。 */
  request: OpsRequest;
  /** 翻訳関数。 */
  t: TFunction;
  /**
   * バグチケットを紐付けられるか（Firestore 発番 かつ PI Div / admin）。紐付け API と同じ条件をページが渡す。
   * legacy シート依頼は関連チケットを持てず（API は 404）、PI Div / admin 以外は API が 403 を返すので、
   * 必ず失敗する入力欄を出さない。
   */
  canLinkBugTicket: boolean;
}

/**
 * 関連チケットカード。request.relatedTickets を種別優先（本件調査結果バグ → 補足対応元 → 補足対応 → 参考バグ → 参考 ops）でソートして
 * 並べる。空なら noneLinked を出す。編集可能なら末尾にバグチケット紐付け欄を出す。
 * @graph-connects none
 */
export function OpsRelatedTicketsCard({
  request,
  t,
  canLinkBugTicket,
}: OpsRelatedTicketsCardProps): ReactNode {
  const sorted = [...request.relatedTickets].sort(
    (a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind],
  );
  return (
    <Card padding="sm">
      <CardHeader title={t('ops.detail.relatedTickets')} />
      <CardContent className="space-y-1.5 text-sm">
        {sorted.length === 0 ? (
          <p className="text-[var(--color-text-muted)]">{t('ops.detail.noneLinked')}</p>
        ) : (
          sorted.map((ticket) => (
            <RelatedTicketRow
              key={`${ticket.target}:${ticket.ref}`}
              requestId={request.id}
              ticket={ticket}
              t={t}
            />
          ))
        )}
        {canLinkBugTicket && <OpsLinkBugTicketForm requestId={request.id} t={t} />}
      </CardContent>
    </Card>
  );
}
